/**
 * Android has no `Keyboard.getResizeMode` (an iOS-only method of `@capacitor/keyboard`). Ionic's keyboard controller
 * and input shims call it whenever an overlay or an input initialises — the order, void and kiosk-exit sheets — and
 * already treat `UNIMPLEMENTED` as "no resize mode". Capacitor's native bridge logs the refused call first, though, so
 * every sheet open left `E/Capacitor/Console … getResizeMode … UNIMPLEMENTED` in logcat (kzd r1 android-qa, DESIGN
 * §16.47).
 *
 * On Android only, the Keyboard plugin's `getResizeMode` is answered locally with `undefined` — exactly what Ionic
 * makes of the native refusal — so the call never crosses the bridge. Every other method is the plugin's own. Run once,
 * before the app boots (Ionic reads `window.Capacitor.Plugins.Keyboard`, which the native bridge has set up by then).
 */
export function guardAndroidKeyboardResizeMode(win: unknown = typeof window !== 'undefined' ? window : undefined): boolean {
  const cap = (win as { Capacitor?: { getPlatform?: () => string; Plugins?: Record<string, unknown> } } | undefined)?.Capacitor;
  if (!cap || cap.getPlatform?.() !== 'android' || !cap.Plugins) {
    return false;
  }
  const keyboard = cap.Plugins['Keyboard'] as Record<string | symbol, unknown> | undefined;
  if (!keyboard || keyboard[GUARDED] === true) {
    return false;
  }
  try {
    cap.Plugins['Keyboard'] = new Proxy(keyboard, {
      get: (target, prop, receiver) => {
        if (prop === 'getResizeMode') {
          return () => Promise.resolve(undefined);
        }
        if (prop === GUARDED) {
          return true;
        }
        return Reflect.get(target, prop, receiver);
      },
    });
    return true;
  } catch {
    return false;
  }
}

const GUARDED = Symbol.for('kz.keyboardResizeGuard');
